import { Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import type { Case } from '../../types';
import SubTabBar from '../../components/SubTabBar';
import AccountsTab from '../../components/tabs/AccountsTab';
import TransactionsTab from '../../components/tabs/TransactionsTab';
import CashFlowTab from '../../components/tabs/CashFlowTab';
import AssetSignalsView from '../../components/tabs/AssetSignalsView';
import MoneyTrailView from '../../components/tabs/MoneyTrailView';
import InvestigationWaste from './InvestigationWaste';

interface Props {
  caseData: Case;
}

export default function InvestigationSection({ caseData }: Props) {
  const caseId = caseData.public_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bank = searchParams.get('bank') || undefined;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <SubTabBar
        basePath={`/cases/${caseData.public_id}/investigation`}
        tabs={[
          { to: 'accounts', label: 'Accounts' },
          { to: 'transactions', label: 'Transactions' },
          { to: 'waste', label: 'Potential Waste' },
          { to: 'signals', label: 'Asset Signals', amber: true },
          { to: 'money-trail', label: 'Money Trail', amber: true },
          { to: 'cash-flow', label: 'Cash Flow' },
        ]}
      />
      <div className="flex-1 overflow-y-auto [scrollbar-gutter:stable] p-6">
        <Routes>
          <Route
            path="accounts"
            element={
              <AccountsTab
                caseId={caseId}
                clientName={caseData.client_name}
                opposingPartyName={caseData.opposing_party_name}
                onNavigateToTransactions={() => navigate(`/cases/${caseData.public_id}/investigation/transactions`)}
                onNavigateToAccountTransactions={(bankLabel: string) =>
                  navigate(`/cases/${caseData.public_id}/investigation/transactions?bank=${encodeURIComponent(bankLabel)}`)
                }
              />
            }
          />
          <Route
            path="transactions"
            element={
              <TransactionsTab
                caseId={caseId}
                defaultBankLabel={bank}
                clientName={caseData.client_name}
                opposingPartyName={caseData.opposing_party_name}
                separationDate={caseData.separation_date}
                subView="all"
              />
            }
          />
          <Route path="waste" element={<InvestigationWaste caseId={caseId} />} />
          <Route path="signals" element={<AssetSignalsView caseId={caseId} />} />
          <Route path="money-trail" element={<MoneyTrailView caseId={caseId} />} />
          <Route path="cash-flow" element={<CashFlowTab caseId={caseId} />} />
          {/* Absolute path: a relative `to` under a splat route re-resolves against the
              unmatched URL and appends forever (…/bogus/transactions/transactions/…). */}
          <Route path="*" element={<Navigate to={`/cases/${caseData.public_id}/investigation/transactions`} replace />} />
        </Routes>
      </div>
    </div>
  );
}
